import type { MouseEventHandler, ReactNode } from 'react';
import { useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useKibanaLogger } from '../../../../shared/hooks/kibana-logger/kibana-logger';
import { DataProviderQueryStatus } from '../DataProvider/types';
import { useTrackFullPagePlotError } from '../FullPageError/full-page-error.hooks';
import { ChartCardShell } from './ChartCardShell';
import { ChartError } from './ChartError';
import { ChartLoader } from './ChartLoader';
import { useChart } from './ChartProvider';
import { useChartLoadingStatus } from './chart-menu/useChartLoadingStatus';

const ChartErrorBoundaryFallback = () => (
  <ChartError message="An error occurred while loading the chart. Please try refreshing the page." />
);

export const ChartFallback = ({
  status,
  children,
  errorMessage,
  onClick,
}: {
  status: DataProviderQueryStatus;
  children?: ReactNode;
  errorMessage?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
}) => {
  const { item } = useChart();
  const { kibanaLogger } = useKibanaLogger();

  useTrackFullPagePlotError(item?.id, status === DataProviderQueryStatus.Error);
  useChartLoadingStatus(status);

  const handleChartError = useCallback(
    (error: Error) => {
      if (kibanaLogger === undefined) return;
      const message = error.stack
        ? `ChartErrorBoundary: ${error.message}\n${error.stack}`
        : `ChartErrorBoundary: ${error.message}`;
      console.error('ChartErrorBoundary', error);
      kibanaLogger.error(message, {
        context: 'ChartErrorBoundary',
      });
    },
    [kibanaLogger]
  );

  if (status === DataProviderQueryStatus.Idle) {
    return (
      <ChartCardShell onClick={onClick}>
        <ChartLoader onClick={onClick} />
      </ChartCardShell>
    );
  }

  if (status === DataProviderQueryStatus.Fetching) {
    return (
      <ChartCardShell onClick={onClick}>
        <ChartLoader onClick={onClick} />
      </ChartCardShell>
    );
  }

  if (status === DataProviderQueryStatus.Error) {
    return (
      <ChartCardShell onClick={onClick}>
        <ChartError message={errorMessage} />
      </ChartCardShell>
    );
  }

  return (
    <ErrorBoundary
      FallbackComponent={ChartErrorBoundaryFallback}
      onError={handleChartError}
    >
      {children}
    </ErrorBoundary>
  );
};
